'use client'

import { useCallback, useEffect, useState } from 'react'

import {
  OperationGroupsDocument,
  type OperationGroup,
} from '@features/playground.graphql'
import { useQuery } from '@shared/apollo'
import { usePathname } from 'next/navigation'

import { ApiReferenceDesktopNavigation } from './ApiReferenceDesktopNavigation/ApiReferenceDesktopNavigation'
import { ApiReferenceMobileNavigation } from './ApiReferenceMobileNavigation/ApiReferenceMobileNavigation'

import type { CommonProps } from './types'
import type { NavigationItem } from '@features/navigation.components/Navigation/types'

/**
 * Props for the ApiReferenceNavigation component.
 */
export interface ApiReferenceNavigationProps
  extends Omit<CommonProps, 'query'> {
  /**
   * Label for the back Button.
   */
  backButtonLabel: string
  /**
   * Optional CSS class name to be applied to the root element.
   */
  className?: string
}

export interface ApiReferenceChildProps extends ApiReferenceNavigationProps {
  additionalNavigationItems?: NavigationItem[]
  /**
   * An optional array of operation groups.
   * Each operation group contains a set of related API operations.
   */
  operationGroups?: OperationGroup[]

  /**
   * The current scope of the API reference.
   * This can be a string representing the scope or null if no scope is selected.
   */
  currentScope: string | null

  /**
   * Function to handle changes in the current scope.
   * This function is called when the user selects a different scope.
   * @param item - The new scope item selected by the user.
   */
  handleScopeChange: (item: string) => void

  /**
   * The current search query string.
   * This represents the user's input in the search field.
   */
  query: string

  /**
   * Function to update the search query string.
   * This function is called when the user changes the search input.
   * @param query - The new search query string entered by the user.
   */
  setQuery: (query: string) => void
  /**
   * The URL to redirect to if the current path is not found in the additional navigation items.
   */
  fallbackBackUrl?: string
}

/**
 * ApiReferenceNavigation component displays a navigation interface for API references.
 * It renders a list of projects, each containing tags and operations, and allows for
 * filtering based on a search query.
 */
export const ApiReferenceNavigation = (props: ApiReferenceNavigationProps) => {
  const { data: operationGroupData } = useQuery(OperationGroupsDocument, {
    onError: () => {},
  })

  const [currentScope, setCurrentScope] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const pathname = usePathname()

  /**
   * Handles the change of the current scope.
   */
  const handleScopeChange = useCallback((item: string) => {
    setCurrentScope(item)
  }, [])

  /**
   * Sets the default scope based on the current path. If the current path does not contain a scope, the first scope is selected.
   */
  useEffect(() => {
    const scopePerPath = pathname.split('/')[2]
    if (operationGroupData && currentScope === null) {
      if (scopePerPath) {
        setCurrentScope(scopePerPath)
      } else {
        setCurrentScope(operationGroupData.operationGroups[0].key)
      }
    }
  }, [operationGroupData, currentScope, pathname])

  return (
    <>
      <ApiReferenceDesktopNavigation
        currentScope={currentScope}
        handleScopeChange={handleScopeChange}
        operationGroups={operationGroupData?.operationGroups}
        query={query}
        setQuery={setQuery}
        {...props}
      />
      <ApiReferenceMobileNavigation
        currentScope={currentScope}
        handleScopeChange={handleScopeChange}
        operationGroups={operationGroupData?.operationGroups}
        query={query}
        setQuery={setQuery}
        {...props}
      />
    </>
  )
}
